import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, ComboBox, useDialog } from '@gokrew/ui';
import { MoneyField } from '@gokrew/ui/form';
import { FormikProvider, useFormik } from 'formik';
import { Trash2 } from 'lucide-react';
import {
  deleteCrmDeal,
  getCrmCompanies,
  getCrmContacts,
  getCrmMembers,
  saveCrmDeal,
} from '@/api/crm';
import type {
  CrmCompany,
  CrmPerson,
  CrmDeal,
  CrmDealInput,
  CrmDealStage,
} from '@/api/crm';
import { useAuth } from '@/context/AuthContext';
import { useCrmFormat, DEAL_STAGES, sixMonthsOut } from './crm-format';
import { CrmCompanyEditor } from './CrmCompanyEditor';
import { CrmContactEditor } from './CrmContactEditor';
import { CrmDeleteDialog } from './CrmDeleteDialog';
import { CrmButton, CrmRequired } from './CrmShared';
import styles from './Crm.module.css';
export const CrmDealEditor = ({
  deal,
  dialogId,
  initialStage = 'qualified',
  onSaved,
  onDeleted,
}: {
  deal?: CrmDeal;
  dialogId?: string;
  initialStage?: CrmDealStage;
  onSaved?: (deal: CrmDeal) => void;
  onDeleted?: () => void;
}) => {
  const { t, money } = useCrmFormat();
  const { user } = useAuth();
  const { openDialog, closeDialog } = useDialog();
  const client = useQueryClient();
  const [companySearch, setCompanySearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<CrmCompany | null>(
    deal?.company ?? null
  );
  const [createdContact, setCreatedContact] = useState<CrmPerson | null>(null);
  const [form, setForm] = useState<CrmDealInput>(
    deal
      ? {
          name: deal.name,
          companyId: deal.companyId,
          contactId: deal.contactId,
          ownerUserId: deal.ownerUserId,
          stage: deal.stage,
          product: deal.product,
          estimatedEmployees: deal.estimatedEmployees,
          atsMrrCents: deal.atsMrrCents,
          expectedCloseDate: deal.expectedCloseDate,
          notes: deal.notes,
          version: deal.version,
        }
      : {
          name: '',
          companyId: '',
          contactId: null,
          ownerUserId: user?.id ?? '',
          stage: initialStage,
          product: 'hris',
          estimatedEmployees: null,
          atsMrrCents: null,
          // New Qualified deals close six months out by default.
          expectedCloseDate:
            initialStage === 'qualified' ? sixMonthsOut() : null,
          notes: '',
        }
  );
  const amount = useFormik<{ mrr: number | '' }>({
    initialValues: {
      mrr: deal?.atsMrrCents == null ? '' : deal.atsMrrCents / 100,
    },
    onSubmit: () => {},
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const companies = useQuery({
    queryKey: ['crm-companies', 'select', companySearch],
    queryFn: () => getCrmCompanies(companySearch, 1),
  });
  const contacts = useQuery({
    queryKey: ['crm', 'contacts', 'select', form.companyId, contactSearch],
    queryFn: () =>
      getCrmContacts({
        companyId: form.companyId || undefined,
        search: contactSearch,
      }),
  });
  // A company with a single contact: that contact is the primary one, unless
  // the user clears it (only offered once per company).
  const autoContactFor = useRef<string | null>(null);
  const onlyContact =
    contacts.data?.total === 1 && !contactSearch
      ? contacts.data.items[0]
      : null;
  useEffect(() => {
    if (
      !onlyContact ||
      onlyContact.companyId !== form.companyId ||
      form.contactId ||
      autoContactFor.current === form.companyId
    )
      return;
    autoContactFor.current = form.companyId;
    setForm((old) =>
      old.companyId === onlyContact.companyId && !old.contactId
        ? { ...old, contactId: onlyContact.id }
        : old
    );
  }, [onlyContact, form.companyId, form.contactId]);
  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
  });
  const companyOptions = (companies.data?.items ?? []).map((company) => ({
    value: company.id,
    label: company.name,
  }));
  if (
    selectedCompany &&
    !companyOptions.some((option) => option.value === selectedCompany.id)
  )
    companyOptions.unshift({
      value: selectedCompany.id,
      label: selectedCompany.name,
    });
  const contactOptions = (contacts.data?.items ?? []).map((contact) => ({
    value: contact.id,
    label: contact.name,
    description: contact.email || undefined,
  }));
  if (
    deal?.contact &&
    deal.companyId === form.companyId &&
    !contactOptions.some((option) => option.value === deal.contact!.id)
  )
    contactOptions.unshift({
      value: deal.contact.id,
      label: deal.contact.name,
      description: deal.contact.email || undefined,
    });
  if (
    createdContact &&
    createdContact.companyId === form.companyId &&
    !contactOptions.some((option) => option.value === createdContact.id)
  )
    contactOptions.unshift({
      value: createdContact.id,
      label: createdContact.name,
      description: createdContact.email || undefined,
    });
  const createLabel = (typed: string, fallback: string) =>
    typed.trim() ? t('crm.create_named', { name: typed.trim() }) : t(fallback);
  const set = <K extends keyof CrmDealInput>(key: K, value: CrmDealInput[K]) =>
    setForm((old) => ({ ...old, [key]: value }));
  const mrr =
    form.product === 'hris'
      ? form.estimatedEmployees == null
        ? null
        : form.estimatedEmployees * 495
      : amount.values.mrr === ''
        ? null
        : Math.round(amount.values.mrr * 100);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const saved = await saveCrmDeal(
        {
          ...form,
          name: form.name.trim(),
          contactId: form.contactId || null,
          atsMrrCents: form.product === 'ats' ? mrr : null,
          expectedCloseDate: form.expectedCloseDate || null,
        },
        deal?.id
      );
      await client.invalidateQueries({ queryKey: ['crm'] });
      onSaved?.(saved);
      if (dialogId) closeDialog(dialogId);
    } catch (err) {
      setError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.conflict'
            : 'crm.save_error'
        )
      );
    } finally {
      setBusy(false);
    }
  };
  const chooseCompany = (id?: string) => {
    const company = companies.data?.items.find((item) => item.id === id);
    if (company) setSelectedCompany(company);
    setForm((old) => ({
      ...old,
      companyId: id || '',
      contactId: null,
      ...(deal
        ? {}
        : {
            product: company?.product === 'ats' ? 'ats' : 'hris',
            estimatedEmployees: company?.estimatedEmployees ?? null,
          }),
    }));
    if (!deal && company)
      void amount.setFieldValue(
        'mrr',
        company.atsMrrCents == null ? '' : company.atsMrrCents / 100
      );
  };
  return (
    <FormikProvider value={amount}>
      <form className={styles.form} onSubmit={submit}>
        <fieldset disabled={busy} className={styles.grid}>
          <label className={styles.fullWidth}>
            <span>
              {t('crm.workspace.deal_name')}
              <CrmRequired />
            </span>
            <Input
              autoFocus
              required
              maxLength={200}
              value={form.name}
              onChange={(event) => {
                const name = event.target.value;
                // Until a company is picked, its search follows the deal name
                // (pre-typed, not created): opening the list offers to
                // create it on top.
                if (!deal && !form.companyId)
                  setCompanySearch((old) =>
                    old === form.name.trim() ? name.trim() : old
                  );
                set('name', name);
              }}
              placeholder={t('crm.workspace.deal_name_placeholder')}
            />
          </label>
          <label>
            <span>
              {t('crm.workspace.company')}
              <CrmRequired />
            </span>
            <ComboBox
              size="sm"
              value={form.companyId}
              allowUnselect={false}
              placeholder={
                (!form.companyId && companySearch) ||
                t('crm.workspace.choose_company')
              }
              initialSearch={form.companyId ? undefined : companySearch}
              options={companyOptions}
              onSearch={setCompanySearch}
              onChange={chooseCompany}
              addNewPosition="top"
              addNewLabel={createLabel(companySearch, 'crm.add_company')}
              onAddNew={() =>
                openDialog({
                  title: t('crm.add_company'),
                  containerClass: styles.editorDialog,
                  content: (
                    <CrmCompanyEditor
                      initialName={companySearch}
                      onSaved={(company) => {
                        setSelectedCompany(company);
                        setForm((old) => ({
                          ...old,
                          companyId: company.id,
                          contactId: null,
                          estimatedEmployees: company.estimatedEmployees,
                        }));
                      }}
                    />
                  ),
                })
              }
            />
          </label>
          <label>
            {t('crm.workspace.primary_contact')}
            <ComboBox
              size="sm"
              value={form.contactId || ''}
              placeholder={t('crm.workspace.choose_contact')}
              options={contactOptions}
              onSearch={setContactSearch}
              onChange={(value) => set('contactId', value || null)}
              addNewPosition="top"
              addNewLabel={createLabel(contactSearch, 'crm.add_contact')}
              onAddNew={() =>
                openDialog({
                  title: t('crm.add_contact'),
                  containerClass: styles.editorDialog,
                  content: (
                    <CrmContactEditor
                      companyId={form.companyId}
                      initialName={contactSearch}
                      onSaved={(contact) => {
                        setCreatedContact(contact);
                        if (contact.companyId === form.companyId)
                          set('contactId', contact.id);
                      }}
                    />
                  ),
                })
              }
            />
          </label>
          <label>
            <span>
              {t('crm.workspace.owner')}
              <CrmRequired />
            </span>
            <ComboBox
              size="sm"
              allowUnselect={false}
              value={form.ownerUserId}
              options={(members.data ?? []).map((member) => ({
                value: member.userId,
                label:
                  [member.firstName, member.lastName]
                    .filter(Boolean)
                    .join(' ') || member.email,
              }))}
              onChange={(value) => set('ownerUserId', value || '')}
            />
          </label>
          <label>
            {t('crm.stage')}
            <ComboBox
              size="sm"
              allowUnselect={false}
              value={form.stage}
              options={DEAL_STAGES.map((value) => ({
                value,
                label: t(`crm.workspace.stages.${value}`),
              }))}
              onChange={(value) => value && set('stage', value)}
            />
          </label>
          <label>
            {t('crm.product')}
            <ComboBox
              size="sm"
              allowUnselect={false}
              value={form.product}
              options={(['hris', 'ats'] as const).map((value) => ({
                value,
                label: t(`crm.products.${value}`),
              }))}
              onChange={(value) => value && set('product', value)}
            />
          </label>
          <label>
            {t('crm.workspace.expected_close')}
            <Input
              type="date"
              value={form.expectedCloseDate || ''}
              onChange={(event) => set('expectedCloseDate', event.target.value)}
            />
          </label>
          {form.product === 'hris' ? (
            <label>
              {t('crm.estimatedEmployees')}
              <Input
                type="number"
                min={0}
                max={10000000}
                step={1}
                value={form.estimatedEmployees ?? ''}
                onChange={(event) =>
                  set(
                    'estimatedEmployees',
                    event.target.value === ''
                      ? null
                      : Number(event.target.value)
                  )
                }
              />
            </label>
          ) : (
            <MoneyField name="mrr" label={t('crm.manual_mrr')} suffix="$" />
          )}
        </fieldset>
        {(companies.isError || contacts.isError || members.isError) && (
          <p role="alert" className={styles.error}>
            {t('crm.load_error')}
          </p>
        )}
        <div className={styles.dealEstimate}>
          <span>
            {t('crm.mrr')}
            <strong>{money(mrr)}</strong>
          </span>
          <span>
            {t('crm.arr')}
            <strong>{money(mrr == null ? null : mrr * 12)}</strong>
          </span>
        </div>
        <label>
          {t('crm.notes')}
          <textarea
            rows={3}
            maxLength={10000}
            value={form.notes || ''}
            onChange={(event) => set('notes', event.target.value)}
          />
        </label>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <div className={styles.actions}>
          {deal && (
            <CrmButton
              type="button"
              variant="destructive"
              IconPrefix={Trash2}
              className={styles.editorDelete}
              disabled={busy}
              onClick={() =>
                openDialog({
                  title: t('crm.delete_deal_title'),
                  content: (
                    <CrmDeleteDialog
                      description={t('crm.delete_deal_description', {
                        name: deal.name,
                      })}
                      conflictKey="crm.delete_changed"
                      onDelete={async () => {
                        await deleteCrmDeal(deal);
                        onDeleted?.();
                      }}
                      editorDialogId={dialogId}
                    />
                  ),
                })
              }
            >
              {t('crm.delete')}
            </CrmButton>
          )}
          <CrmButton
            variant="outline"
            type="button"
            disabled={busy}
            onClick={() => dialogId && closeDialog(dialogId)}
          >
            {t('crm.cancel')}
          </CrmButton>
          <CrmButton
            variant="adminPrimary"
            type="submit"
            disabled={
              busy || !form.name.trim() || !form.companyId || !form.ownerUserId
            }
          >
            {t(busy ? 'crm.saving' : 'crm.save')}
          </CrmButton>
        </div>
      </form>
    </FormikProvider>
  );
};
